// src/services/usageService.js
import { apiRequest } from "./apiService";

/**
 * Usage service for metered features.
 *
 * Responsibilities:
 * - Build stable query strings.
 * - Add idempotency keys for consume calls.
 * - Never mutate caller payloads.
 * - Normalize inc/request_id.
 * - Preserve useful error context for usageSlice / upgrade-limit UI.
 */

const DEFAULT_TIMEOUT_MS = 10000;
const CONSUME_TIMEOUT_MS = 15000;

const qs = (params = {}) => {
  const sp = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null || item === "") return;
        sp.append(key, String(item));
      });
      return;
    }

    sp.set(key, String(value));
  });

  const query = sp.toString();
  return query ? `?${query}` : "";
};

const safeString = (value) => String(value ?? "").trim();

const toPositiveIntOrDefault = (value, fallback = 1) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;

  const i = Math.floor(n);
  return i > 0 ? i : fallback;
};

const getCrypto = () => {
  if (typeof window === "undefined") return null;
  return window.crypto || null;
};

export const ensureRequestId = (seed) => {
  const existing = safeString(seed);
  if (existing) return existing;

  const cryptoRef = getCrypto();

  try {
    if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
      return cryptoRef.randomUUID();
    }
  } catch {
    // ignore
  }

  try {
    if (cryptoRef && typeof cryptoRef.getRandomValues === "function") {
      const buf = new Uint32Array(4);
      cryptoRef.getRandomValues(buf);

      const hex = Array.from(buf)
        .map((n) => n.toString(16).padStart(8, "0"))
        .join("");

      return `req_${Date.now()}_${hex}`;
    }
  } catch {
    // ignore
  }

  return `req_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const clonePlainPayload = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  return { ...payload };
};

const normalizeMeter = (meter) => {
  return safeString(meter);
};

const buildUsagePayload = (payload, { requireRequestId = false } = {}) => {
  const base = clonePlainPayload(payload);

  if (!base) {
    const err = new Error("Usage payload is required.");
    err.code = "USAGE_PAYLOAD_REQUIRED";
    throw err;
  }

  const meter = normalizeMeter(base.meter || base.meterName || base.meter_name);

  if (!meter) {
    const err = new Error("meter is required.");
    err.code = "USAGE_METER_REQUIRED";
    throw err;
  }

  const next = {
    ...base,
    meter,
    inc: toPositiveIntOrDefault(base.inc, 1),
  };

  delete next.meterName;
  delete next.meter_name;

  if (requireRequestId) {
    next.request_id = ensureRequestId(base.request_id || base.requestId);
    delete next.requestId;
  }

  return next;
};

const unwrapErrorPayload = (error) => {
  if (!error || typeof error !== "object") return null;

  if (error.payload && typeof error.payload === "object") return error.payload;
  if (error.response?.data && typeof error.response.data === "object") {
    return error.response.data;
  }
  if (error.data && typeof error.data === "object") return error.data;

  return null;
};

const readStatus = (error) => {
  const status = Number(
    error?.status ??
      error?.statusCode ??
      error?.response?.status ??
      error?.payload?.status ??
      error?.payload?.statusCode
  );

  return Number.isFinite(status) ? status : undefined;
};

const attachUsageErrorContext = (
  error,
  {
    operation,
    endpoint,
    meter,
    requestId,
    payload,
    fallbackMessage = "Usage request failed.",
  } = {}
) => {
  const source = error instanceof Error ? error : new Error(String(error || ""));

  const normalized = new Error(source.message || fallbackMessage);

  try {
    normalized.stack = source.stack;
  } catch {
    // ignore
  }

  const existingPayload = unwrapErrorPayload(source);
  const status = readStatus(source);

  Object.assign(normalized, source, {
    name: source.name || "UsageServiceError",
    operation,
    endpoint,
    meter: meter || payload?.meter || source?.meter || "",
    request_id: requestId || payload?.request_id || source?.request_id || "",
    status,
    payload: existingPayload || source?.payload || null,
    response: source?.response,
    originalError: source,
  });

  return normalized;
};

export const getUsageSnapshot = async (periodKey) => {
  try {
    return await apiRequest(`/usage/snapshot${qs({ periodKey })}`, "GET", null, {
      requiresAuth: true,
      timeoutMs: DEFAULT_TIMEOUT_MS,
    });
  } catch (error) {
    throw attachUsageErrorContext(error, {
      operation: "snapshot",
      endpoint: "/usage/snapshot",
      fallbackMessage: "Failed to load usage snapshot.",
    });
  }
};

export const quoteUsage = async (payload) => {
  const body = buildUsagePayload(payload);
  const endpoint = "/usage/quote";

  try {
    return await apiRequest(endpoint, "POST", body, {
      requiresAuth: true,
      timeoutMs: DEFAULT_TIMEOUT_MS,
    });
  } catch (error) {
    throw attachUsageErrorContext(error, {
      operation: "quote",
      endpoint,
      meter: body.meter,
      payload: body,
      fallbackMessage: "Failed to quote usage.",
    });
  }
};

export const consumeUsage = async (payload) => {
  const body = buildUsagePayload(payload, { requireRequestId: true });
  const endpoint = "/usage/consume";
  const requestId = body.request_id;

  try {
    return await apiRequest(endpoint, "POST", body, {
      requiresAuth: true,
      timeoutMs: CONSUME_TIMEOUT_MS,
      extraHeaders: {
        "X-Idempotency-Key": requestId,
      },
    });
  } catch (error) {
    throw attachUsageErrorContext(error, {
      operation: "consume",
      endpoint,
      meter: body.meter,
      requestId,
      payload: body,
      fallbackMessage: "Failed to consume usage.",
    });
  }
};

export default {
  getUsageSnapshot,
  quoteUsage,
  consumeUsage,
  ensureRequestId,
};